import { BookingStateEnum, BookingStateUrlParameter } from '@xeetr/types/enums'
import { CogIcon, History } from 'lucide-react'
import { useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { useBookings } from '../../../hooks/useBookings'
import { useSidebar } from '../../../hooks/useSidebar'
import { BookingFormValues } from '../../../types/BookingForm'
import { SidebarType } from '../../../types/Sidebar'
import { fromBookingFormValues, toEditFormValues } from '../../../utils/bookingForm'
import Title from '../common/Title'

import ChangelogDialog from './common/dialog/ChangelogDialog'
import { SendMailConfirmDialog } from './common/dialog/SendMailConfirmDialog'

import RecurringBadge from '@/components/commons/RecurringBadge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import { cn } from '@/lib/utils'
import { SidebarStateBookingEdit } from '@/types/SidebarStates'

const BookingEditHeader = () => {
  const { sidebar, updateBooking } = useSidebar()
  const [openChangelogDialog, setOpenChangelogDialog] = useState(false)
  const [pendingState, setPendingState] = useState<BookingStateUrlParameter | null>(null)

  const { handleSubmit, reset, formState } = useFormContext<BookingFormValues>()
  const { update, updateState } = useBookings()

  if (!sidebar.open || sidebar.type !== SidebarType.BOOKING_EDIT) return null

  // Type assertion for booking edit state
  const bookingSidebar = sidebar as SidebarStateBookingEdit
  const { state, id } = bookingSidebar.booking

  const onSaved = (saved?: SidebarStateBookingEdit['booking']) => {
    if (!saved) return
    updateBooking(saved)
    reset(toEditFormValues(saved))
  }

  const saveWithState = (newState: BookingStateEnum, sendEmail: boolean) =>
    handleSubmit((values) =>
      update(fromBookingFormValues(values), sendEmail, 'occurrence', onSaved, { state: newState })
    )()

  // Decline/cancel have no save-with-state path, so unsaved form edits are dropped.
  const changeState = (newState: 'decline' | 'cancel', sendEmail: boolean) => {
    if (!id) return
    // The API defaults sendEmail to true, so always pass it explicitly.
    updateState(id, newState, { sendEmail }, undefined, onSaved)
  }

  const confirmStateChange = (urlState: BookingStateUrlParameter, sendEmail: boolean) => {
    switch (urlState) {
      case 'accept':
        return saveWithState(BookingStateEnum.ACCEPTED, sendEmail)
      case 'decline':
      case 'cancel':
        return changeState(urlState, sendEmail)
    }
  }

  // Only these transitions trigger a customer mail on the API side, so only they ask first.
  const requestStateChange = (newState: BookingStateUrlParameter) => {
    switch (newState) {
      case 'accept':
      case 'decline':
      case 'cancel':
        return setPendingState(newState)
      case 'open':
        return saveWithState(BookingStateEnum.OPEN, false)
      case 'checkin':
        return saveWithState(BookingStateEnum.CHECKEDIN, false)
      case 'checkout':
        return saveWithState(BookingStateEnum.CHECKEDOUT, false)
    }
  }

  const isRecurring = !!sidebar.booking.seriesId

  // Decline/cancel are scope-sensitive for recurring bookings (this occurrence vs. this-and-future)
  // and already have scope-aware dialogs in the footer StateButtons. Offering them here too would
  // let staff bypass that scope choice and send an individual mail outside the mass-action summary.
  const bookingStates: BookingStateUrlParameter[] = isRecurring
    ? ['open', 'accept', 'checkin', 'checkout']
    : ['open', 'accept', 'decline', 'checkin', 'checkout', 'cancel']

  const isActiveState = (stateToCheck: BookingStateUrlParameter) => {
    switch (stateToCheck) {
      case 'open':
        return state?.status === BookingStateEnum.OPEN
      case 'accept':
        return state?.status === BookingStateEnum.ACCEPTED
      case 'decline':
        return state?.status === BookingStateEnum.DECLINED
      case 'checkin':
        return state?.status === BookingStateEnum.CHECKEDIN
      case 'checkout':
        return state?.status === BookingStateEnum.CHECKEDOUT
      case 'cancel':
        return state?.status === BookingStateEnum.CANCELED
      default:
        return false
    }
  }
  const buttons: Record<
    BookingStateUrlParameter,
    { variant: 'default' | 'secondary' | 'destructive'; onClick: () => void; children: string }
  > = {
    open: {
      variant: 'secondary',
      onClick: () => requestStateChange('open'),
      children: 'Offen',
    },
    accept: {
      variant: 'default',
      onClick: () => requestStateChange('accept'),
      children: 'Akzeptieren',
    },
    decline: {
      variant: 'destructive',
      onClick: () => requestStateChange('decline'),
      children: 'Ablehnen',
    },
    checkin: {
      variant: 'default',
      onClick: () => requestStateChange('checkin'),
      children: 'Einchecken',
    },
    checkout: {
      variant: 'destructive',
      onClick: () => requestStateChange('checkout'),
      children: 'Auschecken',
    },
    cancel: {
      variant: 'destructive',
      onClick: () => requestStateChange('cancel'),
      children: 'Stornieren',
    },
  }

  return (
    <div className="flex justify-between">
      <div className="flex items-center gap-2">
        <Title icon="pi-user-edit" title="Reservierung bearbeiten" />
        {isRecurring && <RecurringBadge className="h-4 w-4 shrink-0" />}
      </div>
      <div className="mr-5 flex items-center">
        <ChangelogDialog
          open={openChangelogDialog}
          onOpenChange={setOpenChangelogDialog}
          bookingId={sidebar.booking.id}
        />
        <SendMailConfirmDialog
          open={pendingState !== null}
          onOpenChange={(open) => !open && setPendingState(null)}
          stateLabel={pendingState ? buttons[pendingState].children : ''}
          hasUnsavedChanges={
            (pendingState === 'decline' || pendingState === 'cancel') && formState.isDirty
          }
          confirm={(sendEmail) => {
            if (pendingState) confirmStateChange(pendingState, sendEmail)
            setPendingState(null)
          }}
        />
        <div className="ml-2">
          <Button
            variant="ghost"
            size="icon"
            icon={<History className="h-5 w-5" />}
            onClick={() => setOpenChangelogDialog(true)}
          />
        </div>
        {/* Non-modal so the confirm dialog opened from a menu item doesn't inherit a stale pointer-events lock. */}
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <CogIcon className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent defaultValue={state?.status}>
            {bookingStates.map((buttonName) => {
              const button = buttons[buttonName]
              return (
                <DropdownMenuItem
                  key={buttonName}
                  onClick={button.onClick}
                  className={cn('hover:!bg-primary/80 hover:!text-white', {
                    'bg-primary text-white': isActiveState(buttonName),
                  })}
                >
                  {button.children}
                </DropdownMenuItem>
              )
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}

export default BookingEditHeader
